import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Shield,
  Users,
  ClipboardList,
  Package,
  Mail,
  Globe,
  FileText,
  BarChart3,
  Building2,
  Bot,
  ArrowRight,
  CheckCircle2,
  Star,
  Lock,
  Server,
  UserCheck,
  FileSearch,
  Sparkles,
  Zap,
  TrendingUp,
  Play,
  Database,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const HomePage = () => {
  const [featureToggles, setFeatureToggles] = useState({});

  useEffect(() => {
    fetch(`${API_URL}/api/public/features`)
      .then(res => res.json())
      .then(data => setFeatureToggles(data || {}))
      .catch(() => {});
  }, []);

  const allFeatures = [
    {
      icon: Shield,
      title: 'HIPAA Compliance',
      description: 'Enterprise-grade security with role-based access, audit logging, and encrypted storage.',
      color: 'blue',
      toggleKey: null,
    },
    {
      icon: Globe,
      title: 'Organic SEO Services',
      description: '34,477 organically generated location pages for more than 50 US states.',
      color: 'lime',
      toggleKey: 'organic_seo',
    },
    {
      icon: UserCheck,
      title: 'Compliant Lead Generation',
      description: 'HIPAA-compliant lead capture forms with consent tracking and automated follow-ups.',
      color: 'blue',
      toggleKey: null,
    },
    {
      icon: FileSearch,
      title: 'Doctors Directory',
      description: 'Complete physician directory for every state you advertise in — thousands of verified referral sources ready to connect.',
      color: 'lime',
      toggleKey: null,
    },
    {
      icon: Users,
      title: 'Patient CRM',
      description: 'Complete patient profiles with demographics, insurance, and medical history.',
      color: 'blue',
      toggleKey: 'patient_crm',
    },
    {
      icon: ClipboardList,
      title: 'Order Processing',
      description: 'Complete order lifecycle management with multi-supplier routing and tracking.',
      color: 'lime',
      toggleKey: 'order_processing',
    },
    {
      icon: Package,
      title: 'Product Catalog',
      description: '17 DME categories, 146+ products with HCPCS codes and supplier integration.',
      color: 'blue',
      toggleKey: null,
    },
    {
      icon: Mail,
      title: 'Newsletter System',
      description: 'AI-powered content generation with real SMTP integration.',
      color: 'lime',
      toggleKey: null,
    },
  ];

  const features = allFeatures.filter(f => !f.toggleKey || featureToggles[f.toggleKey] !== false);

  const stats = [
    { value: '17', label: 'DME Categories', icon: Package },
    { value: '146+', label: 'Products', icon: FileText },
    { value: '10', label: 'Suppliers', icon: Truck },
    { value: '34K+', label: 'Location Pages', icon: Globe },
  ];

  const testimonials = [
    {
      quote: "DME R'US transformed our operations. We went from spreadsheets to a fully compliant CRM in weeks.",
      author: 'Dr. Sarah Mitchell',
      role: 'Medical Director',
      company: 'Coastal DME Solutions',
    },
    {
      quote: "The SEO location pages feature is incredible. Our organic traffic increased by 340% in the first quarter.",
      author: 'Michael Chen',
      role: 'Operations Manager',
      company: 'Midwest Medical Equipment',
    },
    {
      quote: "Finally, a CRM that understands DME. The supplier integrations have streamlined our entire process.",
      author: 'Jennifer Roberts',
      role: 'CEO',
      company: 'Premier Healthcare Supplies',
    },
  ];

  const trustBadges = [
    { icon: Lock, text: 'End-to-End Encrypted' },
    { icon: Server, text: 'SOC 2 Compliant' },
    { icon: UserCheck, text: 'RBAC Security' },
    { icon: FileSearch, text: 'Full Audit Trail' },
  ];

  return (
    <div className="min-h-screen overflow-hidden">
      {/* Hero Section */}
      <section data-testid="hero-section" className="relative pt-48 pb-20 lg:pt-56 lg:pb-28 px-6 lg:px-8 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 gradient-animated" />
        <div className="absolute inset-0 grid-pattern opacity-60" />
        
        {/* Floating decorations */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#014DB7]/10 rounded-full blur-3xl animate-pulse-glow" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#84CC16]/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-[#014DB7]/5 rounded-full blur-2xl" />

        <div className="relative max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left Content */}
            <div className="stagger-children">
              <div className="badge-lime mb-8 animate-bounce-subtle">
                <Shield className="w-4 h-4" />
                <span>HIPAA Compliant Platform</span>
              </div>

              <h1 className="text-display text-[#0A1628] mb-8">
                The Future of{' '}
                <span className="gradient-text-blue">DME Management</span>{' '}
                is Here
              </h1>

              <p className="text-subtitle max-w-xl mb-10">
                Enterprise-grade CRM built for DME providers. Manage Leads, track consent, track analytics, 
                and scale your business with full Medicare compliance & grow with us as we build more.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <Link to="/demo">
                  <Button
                    data-testid="hero-demo-btn"
                    className="btn-lift bg-[#014DB7] text-white hover:bg-[#003A8C] rounded-xl px-8 py-6 font-bold text-lg shadow-glow-blue group"
                  >
                    Get Started Free
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/features">
                  <Button
                    data-testid="hero-features-btn"
                    variant="outline"
                    className="btn-lift bg-white text-[#0A1628] border-2 border-[#0A1628]/10 hover:border-[#014DB7] hover:bg-[#014DB7]/5 rounded-xl px-8 py-6 font-bold text-lg group"
                  >
                    <Play className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                    Watch Demo
                  </Button>
                </Link>
              </div>

              {/* Quick stats */}
              <div className="flex items-center gap-8 pt-8 border-t border-[#0A1628]/10">
                <div>
                  <p className="text-3xl font-bold text-[#014DB7]">500+</p>
                  <p className="text-sm text-[#64748B]">Active Users</p>
                </div>
                <div className="w-px h-12 bg-[#0A1628]/10" />
                <div>
                  <p className="text-3xl font-bold text-[#84CC16]">99.9%</p>
                  <p className="text-sm text-[#64748B]">Uptime</p>
                </div>
                <div className="w-px h-12 bg-[#0A1628]/10" />
                <div>
                  <p className="text-3xl font-bold text-[#014DB7]">4.9/5</p>
                  <p className="text-sm text-[#64748B]">User Rating</p>
                </div>
              </div>
            </div>

            {/* Right - Hero Visual */}
            <div className="relative animate-fade-in-right" style={{ animationDelay: '0.4s' }}>
              <div className="relative">
                {/* Main card */}
                <div className="relative bg-white rounded-3xl shadow-hard overflow-hidden border border-[#014DB7]/10 animate-float">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#014DB7] via-[#84CC16] to-[#014DB7]" />
                  <img
                    src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&h=500&fit=crop"
                    alt="Medical professional using tablet"
                    className="w-full h-auto"
                  />
                  
                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/60 via-transparent to-transparent" />
                  
                  {/* Stats overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="glass rounded-2xl p-5">
                      <div className="grid grid-cols-3 gap-4">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-[#014DB7]">100+</p>
                          <p className="text-xs text-[#64748B] font-medium">API Endpoints</p>
                        </div>
                        <div className="text-center border-x border-[#0A1628]/10">
                          <p className="text-2xl font-bold text-[#84CC16]">50</p>
                          <p className="text-xs text-[#64748B] font-medium">States Covered</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-[#014DB7]">24/7</p>
                          <p className="text-xs text-[#64748B] font-medium">Support</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating badges */}
                <div className="absolute -top-4 -right-4 bg-[#84CC16] text-[#0A1628] rounded-2xl px-5 py-3 shadow-glow-lime animate-bounce-subtle">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    <span className="font-bold">AI Powered</span>
                  </div>
                </div>

                <div className="absolute -bottom-4 -left-4 bg-[#014DB7] text-white rounded-2xl px-5 py-3 shadow-glow-blue">
                  <div className="flex items-center gap-2">
                    <Zap className="w-5 h-5" />
                    <span className="font-bold">Real-time Sync</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="py-6 bg-[#0A1628]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-8 lg:gap-16">
            {trustBadges.map((badge, index) => (
              <div key={index} className="flex items-center gap-3 text-white/90">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                  <badge.icon className="w-5 h-5 text-[#84CC16]" />
                </div>
                <span className="text-sm font-semibold">{badge.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section data-testid="stats-section" className="py-24 bg-white relative overflow-hidden">
        <div className="absolute inset-0 dot-pattern opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16 stagger-children">
            <div className="badge-blue mx-auto mb-6">
              <TrendingUp className="w-4 h-4" />
              <span>Platform Stats</span>
            </div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0A1628] mb-6">
              Built for <span className="gradient-text-mixed">Scale</span>
            </h2>
            <p className="text-subtitle max-w-2xl mx-auto">
              Everything you need to manage your DME business, all in one powerful platform.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 stagger-grid">
            {[
              { value: '17', label: 'DME Categories', color: 'blue' },
              { value: '146+', label: 'Products', color: 'lime' },
              { value: '10', label: 'Suppliers', color: 'blue' },
              { value: '34K+', label: 'Location Pages', color: 'lime' },
            ].map((stat, index) => (
              <div
                key={index}
                className={`card-hover relative p-8 lg:p-10 rounded-3xl border-2 ${
                  stat.color === 'blue' 
                    ? 'bg-[#014DB7]/5 border-[#014DB7]/20 hover:border-[#014DB7]' 
                    : 'bg-[#84CC16]/5 border-[#84CC16]/20 hover:border-[#84CC16]'
                } text-center overflow-hidden shine-effect`}
              >
                <p className={`text-5xl lg:text-6xl font-extrabold mb-3 ${
                  stat.color === 'blue' ? 'text-[#014DB7]' : 'text-[#84CC16]'
                }`}>
                  {stat.value}
                </p>
                <p className="text-[#64748B] font-semibold">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section data-testid="features-section" className="py-24 bg-[#F8FAFC] relative">
        <div className="absolute inset-0 grid-pattern opacity-50" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16 stagger-children">
            <div className="badge-lime mx-auto mb-6">
              <Sparkles className="w-4 h-4" />
              <span>Powerful Features</span>
            </div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0A1628] mb-6">
              Everything You <span className="gradient-text-blue">Need</span>
            </h2>
            <p className="text-subtitle max-w-2xl mx-auto">
              Comprehensive features designed specifically for DME providers.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-grid">
            {features.map((feature, index) => (
              <div
                key={index}
                className="card-hover group bg-white rounded-3xl p-8 border-2 border-transparent hover:border-[#014DB7]/20 shadow-soft hover:shadow-hard shine-effect"
              >
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 transition-all duration-300 ${
                  feature.color === 'blue'
                    ? 'bg-[#014DB7]/10 group-hover:bg-[#014DB7] group-hover:shadow-glow-blue'
                    : 'bg-[#84CC16]/10 group-hover:bg-[#84CC16] group-hover:shadow-glow-lime'
                }`}>
                  <feature.icon className={`w-8 h-8 transition-colors duration-300 ${
                    feature.color === 'blue'
                      ? 'text-[#014DB7] group-hover:text-white'
                      : 'text-[#84CC16] group-hover:text-white'
                  }`} />
                </div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3 group-hover:text-[#014DB7] transition-colors">
                  {feature.title}
                </h3>
                <p className="text-[#64748B] leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center mt-14">
            <Link to="/features">
              <Button
                data-testid="view-all-features-btn"
                className="btn-lift bg-[#0A1628] text-white hover:bg-[#014DB7] rounded-xl px-10 py-5 font-bold text-lg shadow-medium group"
              >
                View All Features
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* AI Features Section */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="absolute right-0 top-0 w-1/2 h-full bg-gradient-to-l from-[#014DB7]/5 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="stagger-children">
              <div className="badge-blue mb-6">
                <Bot className="w-4 h-4" />
                <span>AI-Powered</span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold text-[#0A1628] mb-6">
                <span className="gradient-text-mixed">Intelligent</span> Automation
              </h2>
              <p className="text-subtitle mb-10">
                Leverage cutting-edge AI to streamline your workflow. Generate content, 
                edit documents, and automate tasks with natural language.
              </p>
              <ul className="space-y-5">
                {[
                  'AI Newsletter Writer with GPT-5.2',
                  'Natural language document editing',
                  'Smart product image search',
                  'Automated content generation',
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-4 group">
                    <div className="w-10 h-10 rounded-xl bg-[#84CC16]/20 flex items-center justify-center group-hover:bg-[#84CC16] transition-colors">
                      <CheckCircle2 className="w-5 h-5 text-[#84CC16] group-hover:text-white transition-colors" />
                    </div>
                    <span className="text-[#0A1628] font-medium text-lg">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative animate-fade-in-right">
              <div className="relative rounded-3xl overflow-hidden shadow-hard border-2 border-[#014DB7]/10">
                <img
                  src="https://images.unsplash.com/photo-1631039302217-5a6c56371e86?w=800&h=600&fit=crop"
                  alt="Healthcare team collaboration"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#014DB7]/20 to-transparent" />
              </div>
              {/* Decorative element */}
              <div className="absolute -z-10 -bottom-6 -right-6 w-full h-full bg-[#84CC16]/20 rounded-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section data-testid="testimonials-section" className="py-24 bg-[#0A1628] relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#014DB7]/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#84CC16]/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16 stagger-children">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Trusted by <span className="text-[#84CC16]">Healthcare</span> Leaders
            </h2>
            <p className="text-xl text-white/70 max-w-2xl mx-auto">
              See what our customers have to say about DME R'US.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 stagger-grid">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="card-hover bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10 hover:border-[#84CC16]/50 group"
              >
                <div className="flex items-center gap-1 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-[#84CC16] fill-[#84CC16]" />
                  ))}
                </div>
                <p className="text-white/90 leading-relaxed mb-8 text-lg">
                  "{testimonial.quote}"
                </p>
                <div className="pt-6 border-t border-white/10">
                  <p className="font-bold text-white">{testimonial.author}</p>
                  <p className="text-sm text-white/60">{testimonial.role}</p>
                  <p className="text-sm text-[#84CC16] font-medium">{testimonial.company}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Features Included */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-[#0A1628] mb-6">
              Core Features <span className="gradient-text-blue">Included</span>
            </h2>
          </div>
        </div>
        <div className="relative">
          <div className="flex animate-marquee gap-6 w-max">
            {[
              { icon: Server, title: 'E2 S3-Compliant Cloud Storage', description: 'Enterprise-grade encrypted cloud storage that meets S3 compliance standards for all your sensitive documents and files.', color: 'blue' },
              { icon: FileSearch, title: 'Doctors Directory', description: 'Full doctors directory in the main navigation for all users — easy research & connection with physicians across every state.', color: 'lime' },
              { icon: Star, title: 'Testimonial & Review Collection', description: 'Collect and showcase patient testimonials and reviews to build trust and credibility for your DME business.', color: 'blue' },
              { icon: Package, title: 'Product Catalog', description: 'Fully built-out catalog of what your company sells — SEO-enhancing product pages that drive organic traffic and conversions.', color: 'lime' },
              { icon: Shield, title: 'Consent Failovers', description: 'Tracks every lead and consent proof to market and sell to your organic leads — never lose compliance documentation.', color: 'blue' },
              { icon: Bot, title: 'Live Chat', description: 'Built-in live chat for your sales staff to communicate in real time — never lose a lead to slow response times.', color: 'lime' },
              { icon: Database, title: 'Database Backup', description: 'Full database backup at the press of a button — protect your business data with instant one-click backups.', color: 'blue' },
              { icon: Server, title: 'E2 S3-Compliant Cloud Storage', description: 'Enterprise-grade encrypted cloud storage that meets S3 compliance standards for all your sensitive documents and files.', color: 'blue' },
              { icon: FileSearch, title: 'Doctors Directory', description: 'Full doctors directory in the main navigation for all users — easy research & connection with physicians across every state.', color: 'lime' },
              { icon: Star, title: 'Testimonial & Review Collection', description: 'Collect and showcase patient testimonials and reviews to build trust and credibility for your DME business.', color: 'blue' },
              { icon: Package, title: 'Product Catalog', description: 'Fully built-out catalog of what your company sells — SEO-enhancing product pages that drive organic traffic and conversions.', color: 'lime' },
              { icon: Shield, title: 'Consent Failovers', description: 'Tracks every lead and consent proof to market and sell to your organic leads — never lose compliance documentation.', color: 'blue' },
              { icon: Bot, title: 'Live Chat', description: 'Built-in live chat for your sales staff to communicate in real time — never lose a lead to slow response times.', color: 'lime' },
              { icon: Database, title: 'Database Backup', description: 'Full database backup at the press of a button — protect your business data with instant one-click backups.', color: 'blue' },
            ].map((item, index) => (
              <div 
                key={index} 
                className={`flex-shrink-0 w-[280px] text-center p-10 rounded-3xl border-2 transition-all ${
                  item.color === 'blue'
                    ? 'bg-[#014DB7]/5 border-[#014DB7]/10 hover:border-[#014DB7]'
                    : 'bg-[#84CC16]/5 border-[#84CC16]/10 hover:border-[#84CC16]'
                } group`}
              >
                <div className={`w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-6 transition-all ${
                  item.color === 'blue'
                    ? 'bg-white shadow-soft group-hover:bg-[#014DB7] group-hover:shadow-glow-blue'
                    : 'bg-white shadow-soft group-hover:bg-[#84CC16] group-hover:shadow-glow-lime'
                }`}>
                  <item.icon className={`w-10 h-10 transition-colors ${
                    item.color === 'blue'
                      ? 'text-[#014DB7] group-hover:text-white'
                      : 'text-[#84CC16] group-hover:text-white'
                  }`} />
                </div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3">{item.title}</h3>
                <p className="text-[#64748B] text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-24 relative overflow-hidden">
        {/* Gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#014DB7] via-[#003A8C] to-[#0A1628]" />
        <div className="absolute inset-0 grid-pattern opacity-10" />
        
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#84CC16]/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#0066FF]/20 rounded-full blur-3xl" />
        
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center stagger-children">
          <div className="badge-lime mx-auto mb-8">
            <Zap className="w-4 h-4" />
            <span>Get Started Today</span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-8">
            Ready to Transform Your{' '}
            <span className="text-[#84CC16]">DME Business?</span>
          </h2>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
            Join 500+ healthcare providers who trust DME R'US for patient management, 
            order tracking, and compliance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/demo">
              <Button
                data-testid="cta-demo-btn"
                className="btn-lift bg-[#84CC16] text-[#0A1628] hover:bg-[#A3E635] rounded-xl px-10 py-6 font-bold text-lg shadow-glow-lime group"
              >
                Start Free Trial
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button
                data-testid="cta-contact-btn"
                variant="outline"
                className="btn-lift bg-transparent text-white border-2 border-white/30 hover:border-white hover:bg-white/10 rounded-xl px-10 py-6 font-bold text-lg"
              >
                Talk to Sales
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

// Add missing import for Truck
const Truck = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/>
    <path d="M15 18H9"/>
    <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/>
    <circle cx="17" cy="18" r="2"/>
    <circle cx="7" cy="18" r="2"/>
  </svg>
);

export default HomePage;
